import React, { useState } from 'react';
import { serviceLogin, serviceSendPasscode } from '../../service/AuthService';
import './LoginComponent.css';

interface LoginComponentProps {
  onLoginSuccess: () => void;
}

const LoginComponent = ({ onLoginSuccess }: LoginComponentProps): React.ReactElement => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendPasscodeLoading, setSendPasscodeLoading] = useState(false);
  const [isSendPasscode, setIsSendPasscode] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Password validation function
  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    // For 6-digit passcode validation
    if (!/^\d{6}$/.test(password)) {
      setPasswordError('Password must be exactly 6 digits');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      validateEmail(value);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (passwordError) {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate both fields
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    
    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);

    try {
      const response = await serviceLogin(email, password);
      localStorage.setItem('accessToken', response.data.jwtToken);
      onLoginSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasscode = async () => {
    if (!validateEmail(email)) {
      return;
    }
    
    try {
      setSendPasscodeLoading(true);
      const response = await serviceSendPasscode(email);
      setIsSendPasscode(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send passcode. Please try again.');
    } finally {
      setSendPasscodeLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="5sentence.com" width="64" height="64" className="login-logo" />
          <h2 className="login-title">5sentence.com</h2>
          <p className="login-subtitle">Learn English with AI</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email"
              className={`form-input ${emailError ? 'form-input-error' : ''}`}
              required
            />
            {emailError && (
              <div className="error-message">
                {emailError}
              </div>
            )}
          </div>
          <div className="form-group">
            <div className="password-header">
              <label htmlFor="password" className="form-label">6 Digits Passcode</label>
              {isSendPasscode ? (
                <div className="success-message">
                  Successfully sent to above email.
                </div>
              ) : (
                <button 
                  type="button"
                  className="send-passcode-btn"
                  onClick={handleSendPasscode}
                >
                  send new passcode to my email
                </button>
              )}
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter 6-digit passcode"
              className={`form-input ${passwordError ? 'form-input-error' : ''}`}
              maxLength={6}
              required
            />
            {passwordError && (
              <div className="error-message">
                {passwordError}
              </div>
            )}
          </div>
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          <button
            type="submit"
            className="login-button"
            disabled={loading || sendPasscodeLoading || !!emailError || !!passwordError}
          >
            {loading ? 'Logging in...' : sendPasscodeLoading ? 'Sending passcode...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};
export default LoginComponent;
